import moment from 'moment/moment'
import { useMutation } from '@apollo/client'
import { colors } from '@resiliatech/birdsong'
import UPSERT_CHECKLIST_ITEM from 'Apollo/Mutations/UpsertChecklistItem'
import UPSERT_RECURRING_CHECKLIST_ITEM from 'Apollo/Mutations/UpsertRecurringChecklistItem'
import { GOALS } from 'Apollo/Queries/Goals'
import GET_TASKS from 'Apollo/Queries/GetTasks'
import {
  ChecklistItem,
  RecurringChecklistItem,
  TaskRecurrenceType,
} from 'Generated/graphql-types'
import { Course, Resource, ResourceType } from 'Generated/academy-graphql-types'
import { ChecklistItemWithResourceInfo, User } from 'TypeDefs/Goal'
import useNonprofitOrgTeam from 'Hooks/useNonprofitOrgTeam/useNonprofitOrgTeam'
import { useCallback } from 'react'
import {
  DayOfTheMonthValue,
  DayOfTheWeekValue,
  OrdinalWeekValue,
  TaskDetailsWithRecurrence,
  TimeUnitSubTypeEnum,
} from './types'
import { TabFilterKeyEnum } from '../../ImpactReportDashboard/types'

export const useAssigneeEmail = () => {
  const { teamMap = {} } = useNonprofitOrgTeam()

  return useCallback(
    (user: User | null) => (user?.id ? teamMap[user.id]?.email : null),
    [teamMap]
  )
}

const DATE_PICKER_FORMAT = 'YYYY-MM-DD'
const MSG_DATE_FORMAT = 'MM/DD/YYYY'
const NUM_DAYS_IN_YEAR = 365
const ERROR_MESSAGE = 'unknown (unable to determine date)'
const INVALID_RANGE_MESSAGE = 'not be created due to an invalid date range'

export const useTaskUpdate = (goalId: number, nonprofitOrgId: number) => {
  const [upsertChecklistItem, { loading, error }] = useMutation(
    UPSERT_CHECKLIST_ITEM
  )

  const upsertTask = async (
    taskInfoToSave: Partial<ChecklistItemWithResourceInfo>
  ) => {
    const itemInfo = {
      goalId,
      id: taskInfoToSave.id,
      title: taskInfoToSave.title,
      isCompleted: taskInfoToSave.isCompleted,
      dueDate: taskInfoToSave.dueDate,
      linkText: taskInfoToSave.linkText || null,
      linkUrl: taskInfoToSave.linkUrl || null,
      ownerId: taskInfoToSave?.owner?.id,
      resourceId: taskInfoToSave?.resource?.id ?? null,
      checklistItemGroupId: taskInfoToSave?.checklistItemGroupId ?? null,
    }

    const { data: taskData } = await upsertChecklistItem({
      variables: {
        itemInfo,
        nonprofitOrgId,
      },
      refetchQueries: [
        { query: GET_TASKS },
        {
          query: GOALS,
          variables: { goalId: String(goalId), nonprofitOrgId },
        },
      ],
    })

    return taskData?.upsertChecklistItem
  }

  return { upsertTask, loading, error }
}

export const useUpsertRecurringTask = (
  goalId: number,
  nonprofitOrgId: number
) => {
  const [upsertRecurringChecklistItem, { loading, error }] = useMutation(
    UPSERT_RECURRING_CHECKLIST_ITEM
  )

  const upsertRecurringTask = async (
    taskInfoToSave: Partial<ChecklistItemWithResourceInfo>,
    recurrenceOptions: RecurringChecklistItem
  ) => {
    const {
      dayOfMonth,
      dayOfWeek,
      endDate,
      frequency,
      month,
      recurrenceType,
      startDate,
      weekOfMonth,
    } = recurrenceOptions

    const RESOLVER_DATE_FORMAT = 'YYYY-MM-DD'

    const { data: taskData } = await upsertRecurringChecklistItem({
      variables: {
        recurringItemInfo: {
          checklistItemInput: {
            dueDate: taskInfoToSave.dueDate,
            goalId,
            id: taskInfoToSave.id,
            isCompleted: taskInfoToSave.isCompleted,
            linkText: taskInfoToSave.linkText || null,
            linkUrl: taskInfoToSave.linkUrl || null,
            ownerId: taskInfoToSave.owner?.id,
            resourceId: taskInfoToSave?.resource?.id ?? null, // Detach resource if null,
            title: taskInfoToSave.title,
          },
          recurringChecklistItem: {
            dayOfMonth: Number.isNaN(Number(dayOfMonth))
              ? dayOfMonth // 'Last'
              : Number(dayOfMonth),
            dayOfWeek: Number(dayOfWeek),
            endDate: moment(endDate).format(RESOLVER_DATE_FORMAT),
            frequency: Number(frequency),
            month,
            recurrenceType,
            startDate: moment(startDate).format(RESOLVER_DATE_FORMAT),
            weekOfMonth: Number.isNaN(Number(weekOfMonth))
              ? weekOfMonth // 'Last'
              : Number(weekOfMonth),
          },
        },
        nonprofitOrgId,
      },
      refetchQueries: [
        {
          query: GOALS,
          variables: { goalId: String(goalId), nonprofitOrgId },
        },
      ],
    })

    return taskData?.upsertRecurringChecklistItem
  }

  return { upsertRecurringTask, loading, error }
}

const getOrdinalSuffix = (number: number) => {
  const j = number % 10
  const k = number % 100
  if (j === 1 && k !== 11) return `${number}st`
  if (j === 2 && k !== 12) return `${number}nd`
  if (j === 3 && k !== 13) return `${number}rd`
  return `${number}th`
}

const getOrdinalWeek = (weekOfMonth: OrdinalWeekValue) => {
  switch (weekOfMonth) {
    case '1':
      return 'first'
    case '2':
      return 'second'
    case '3':
      return 'third'
    case '4':
      return 'fourth'
    default:
      return 'last'
  }
}

const getNextDateForWeekly = ({
  startDate,
  endDate,
  dayValue,
}: {
  startDate: string
  endDate: string | null
  dayValue: number
}) => {
  try {
    const start = moment(startDate)
    const end = moment(endDate)

    // Find the number of days until the next occurrence of the specified day
    const daysUntilNextDay = (dayValue - start.day() + 7) % 7
    let nextDate = start.add(daysUntilNextDay, 'days')

    // If the specified day is the same as the given date's day, return the given date
    if (daysUntilNextDay === 0) nextDate = moment(startDate)

    if (end && nextDate.isAfter(end)) return INVALID_RANGE_MESSAGE
    return nextDate.format(MSG_DATE_FORMAT)
  } catch {
    return ERROR_MESSAGE
  }
}

export const getNextDateForCalendarDay = ({
  startDate,
  dayOfMonth,
}: {
  startDate: string
  dayOfMonth: DayOfTheMonthValue
}) => {
  const dateObj = moment(startDate)
  try {
    if (dayOfMonth === 'Last') {
      return dateObj.endOf('month').format(MSG_DATE_FORMAT)
    }

    // If day of the month is the same as the start date's calendar day, return the start date
    if (Number(dayOfMonth) === dateObj.date())
      return dateObj.format(MSG_DATE_FORMAT)

    // If the day of the month is before the start date's calendar day, fast-forward to next month
    if (Number(dayOfMonth) < dateObj.date())
      return dateObj
        .add('1', 'month')
        .date(Number(dayOfMonth))
        .format(MSG_DATE_FORMAT)

    return dateObj.date(Number(dayOfMonth)).format(MSG_DATE_FORMAT)
  } catch {
    return ERROR_MESSAGE
  }
}

export const getRecurrenceValuesToSendMutation = ({
  dayOfMonth,
  dayOfWeek,
  weekOfMonth,
  startDate,
  endDate,
  timeUnitSubType,
  recurrenceType,
  frequency,
}: {
  dayOfMonth: DayOfTheMonthValue
  dayOfWeek: DayOfTheWeekValue
  weekOfMonth: OrdinalWeekValue
  startDate: string
  endDate: string | null
  timeUnitSubType: TimeUnitSubTypeEnum
  recurrenceType: TaskRecurrenceType
  frequency: number
}) => {
  // Base recurrence options
  const recurrenceOptions: RecurringChecklistItem = {
    startDate,
    endDate,
    recurrenceType,
    frequency,
  }

  // Handle different recurrence types
  switch (recurrenceType) {
    case TaskRecurrenceType.Weekly:
      return { ...recurrenceOptions, dayOfWeek: Number(dayOfWeek) }

    case TaskRecurrenceType.Monthly: {
      if (timeUnitSubType === TimeUnitSubTypeEnum.dayOfMonth) {
        const isLastDayOfMonth = dayOfMonth === 'Last'
        return {
          ...recurrenceOptions,
          dayOfMonth: isLastDayOfMonth ? 'Last' : Number(dayOfMonth),
        }
      }
      if (timeUnitSubType === TimeUnitSubTypeEnum.dayOfWeek) {
        const isLastWeekOfMonth = weekOfMonth === 'Last'
        return {
          ...recurrenceOptions,
          dayOfWeek: Number(dayOfWeek),
          weekOfMonth: isLastWeekOfMonth ? 'Last' : Number(weekOfMonth),
        }
      }
      return recurrenceOptions
    }

    case TaskRecurrenceType.Quarterly:
      return { ...recurrenceOptions, dayOfMonth: Number(dayOfMonth) }

    case TaskRecurrenceType.Annually:
    default:
      return recurrenceOptions
  }
}

export const getNextDateForMonthly = ({
  dayOfMonth,
  dayOfWeek,
  weekOfMonth,
  startDate,
  endDate,
  timeUnitSubType,
}: {
  dayOfMonth: DayOfTheMonthValue
  dayOfWeek: DayOfTheWeekValue
  weekOfMonth: OrdinalWeekValue
  startDate: string
  endDate: string | null
  timeUnitSubType: TimeUnitSubTypeEnum
}) => {
  const start = moment(startDate, DATE_PICKER_FORMAT)
  const end = endDate ? moment(endDate, DATE_PICKER_FORMAT) : null

  switch (timeUnitSubType) {
    case TimeUnitSubTypeEnum.dayOfMonth: {
      return getNextDateForCalendarDay({ startDate, dayOfMonth })
    }
    case TimeUnitSubTypeEnum.dayOfWeek: {
      // Termination condition
      let daysEvaluated = 0
      let targetOrdinal = weekOfMonth
      if (weekOfMonth === 'Last') {
        const startOfMonth = start.clone().startOf('month')
        const endOfMonth = start.clone().endOf('month')

        let count = 0
        const currentDay = startOfMonth.clone()

        while (currentDay <= endOfMonth) {
          if (currentDay.day() === Number(dayOfWeek)) count += 1
          currentDay.add(1, 'day')
        }
        targetOrdinal = count.toString() as OrdinalWeekValue
      }

      const firstDayOfMonth = start.clone().startOf('month')
      const lastDayOfMonth = start.clone().endOf('month')
      const targetDay = Number(dayOfWeek)
      let ordinalPosition = 1

      while (daysEvaluated < NUM_DAYS_IN_YEAR) {
        for (let i = 1; i <= lastDayOfMonth.date(); i++) {
          const dateObj = firstDayOfMonth.clone().date(i)
          const matchesTargetDay = dateObj.day() === targetDay
          const matchesOrdinalWeek = ordinalPosition === Number(targetOrdinal)
          const isEqualToOrAfterStartDate =
            dateObj.isAfter(start) || dateObj.isSame(start)
          const isEqualToOrBeforeUntilDate = end
            ? dateObj.isBefore(end) || dateObj.isSame(end)
            : true

          if (matchesTargetDay) {
            if (matchesOrdinalWeek && isEqualToOrAfterStartDate) {
              return isEqualToOrBeforeUntilDate
                ? dateObj.format(MSG_DATE_FORMAT)
                : INVALID_RANGE_MESSAGE
            }
            // Increment the ordinal position to check the next occurrence
            ordinalPosition += 1
          }

          // No suitable match in current month. Reset ordinal and increment the month
          if (i === lastDayOfMonth.date() - 1) {
            ordinalPosition = 1
            firstDayOfMonth.add(1, 'month')
            lastDayOfMonth.add(1, 'month')
          }

          // Increment value for termination condition
          daysEvaluated += 1
        }
      }

      return ERROR_MESSAGE
    }
    default:
      return ERROR_MESSAGE
  }
}

const getNextOccurrence = ({
  dayOfWeek,
  dayOfMonth,
  weekOfMonth,
  startDate,
  recurrenceType,
  timeUnitSubType,
  endDate,
}: {
  dayOfWeek: DayOfTheWeekValue
  dayOfMonth: DayOfTheMonthValue
  weekOfMonth: OrdinalWeekValue
  startDate: string | null
  recurrenceType: TaskRecurrenceType
  timeUnitSubType: TimeUnitSubTypeEnum
  endDate: string | null
}) => {
  if (!startDate) return MSG_DATE_FORMAT

  switch (recurrenceType) {
    case TaskRecurrenceType.Weekly: {
      return getNextDateForWeekly({
        startDate,
        endDate,
        dayValue: Number(dayOfWeek),
      })
    }
    case TaskRecurrenceType.Monthly: {
      return getNextDateForMonthly({
        dayOfMonth,
        dayOfWeek,
        weekOfMonth,
        startDate,
        endDate,
        timeUnitSubType,
      })
    }
    case TaskRecurrenceType.Quarterly: {
      return getNextDateForCalendarDay({
        startDate,
        dayOfMonth,
      })
    }
    case TaskRecurrenceType.Annually:
      return moment(startDate).format(MSG_DATE_FORMAT)
    default:
      return ERROR_MESSAGE
  }
}

export const buildRecurrenceMessage = (
  taskData: TaskDetailsWithRecurrence
): string => {
  const {
    dayOfMonth,
    dayOfWeek,
    frequency,
    weekOfMonth,
    startDate,
    recurrenceType,
    timeUnitSubType,
    endDate,
  } = taskData

  const recurrenceTypeMap = {
    [TaskRecurrenceType.Weekly]: 'week',
    [TaskRecurrenceType.Monthly]: 'month',
    [TaskRecurrenceType.Quarterly]: 'quarter',
    [TaskRecurrenceType.Annually]: 'year',
  }

  const endDateIsBeforeStartDate =
    endDate && moment(endDate).isBefore(startDate)
  const isEndDateMissing = !endDate

  try {
    const numTimeUnitsMsg = frequency === '1' ? '' : frequency
    const timeUnitSingleOrPlural = `${recurrenceTypeMap[recurrenceType]}${
      frequency === '1' ? '' : 's'
    }`
    const parsed = parseInt(dayOfMonth, 10) // 1-28 or 'Last'
    const isInt = !Number.isNaN(parsed)
    const day = isInt ? getOrdinalSuffix(parsed) : 'last day of the month'

    const nextOccurrence = getNextOccurrence({
      dayOfWeek,
      dayOfMonth,
      weekOfMonth,
      startDate,
      recurrenceType,
      timeUnitSubType,
      endDate,
    })

    let timeUnitMsg = ''
    switch (recurrenceType) {
      case TaskRecurrenceType.Weekly:
        timeUnitMsg = `${timeUnitSingleOrPlural} on ${moment()
          .day(dayOfWeek)
          .format('dddd')}`
        break
      case TaskRecurrenceType.Monthly: {
        if (timeUnitSubType === TimeUnitSubTypeEnum.dayOfMonth) {
          timeUnitMsg = `${timeUnitSingleOrPlural} on the ${day} `
        } else if (timeUnitSubType === TimeUnitSubTypeEnum.dayOfWeek) {
          timeUnitMsg = `${timeUnitSingleOrPlural} on the ${getOrdinalWeek(
            weekOfMonth
          )} ${moment().day(dayOfWeek).format('dddd')}`
        }
        break
      }
      case TaskRecurrenceType.Quarterly:
        timeUnitMsg = `${timeUnitSingleOrPlural} on the ${day}`
        break
      default:
        timeUnitMsg = timeUnitSingleOrPlural
    }

    const startDateMsg = startDate
      ? moment(startDate).format(MSG_DATE_FORMAT)
      : MSG_DATE_FORMAT

    const untilDateMsg = endDate
      ? moment(endDate).format(MSG_DATE_FORMAT)
      : MSG_DATE_FORMAT

    let message = `
        This task will repeat every ${numTimeUnitsMsg} ${timeUnitMsg} starting ${startDateMsg}
        until ${untilDateMsg}.`

    const errStyles = `color: ${colors.feedback.errorDark};`
    if (endDateIsBeforeStartDate) {
      message += ` <span style="${errStyles}">Your next occurrence can not be calculated because the
                   Until Date is <span style="font-weight: bold">before</span> the start date.`
    } else if (isEndDateMissing) {
      message += ` <span style="${errStyles}">Your next occurrence can not be calculated because the
                   Until Date is missing.</span>`
    } else {
      message += ` Your first occurrence would be ${nextOccurrence}.`
    }

    return message
  } catch {
    return ''
  }
}

export const getTasksWithResources = ({
  resources,
  tasks,
}: {
  resources: Resource[]
  tasks: ChecklistItem[]
}): ChecklistItemWithResourceInfo[] =>
  tasks.map((task) => {
    const resource = resources.find(
      (r: { id: string }) =>
        r.id.toLowerCase() === task.resourceId?.toLowerCase()
    )
    return {
      goalId: task.goalId,
      lastUpdatedUser: task.lastUpdatedUser,
      createdAt: task.createdAt,
      title: task.title,
      id: task.id,
      isCompleted: task.isCompleted ?? false,
      owner: task.owner ?? null,
      resource: resource ?? null,
      updatedAt: task.updatedAt,
      dueDate: task.dueDate ?? null,
      linkText: task.linkText ?? null,
      linkUrl: task.linkUrl ?? null,
      checklistItemGroupId: task.checklistItemGroupId ?? null,
    }
  })

export const getFilteredTasksByTab = (
  tasks: ChecklistItemWithResourceInfo[],
  userId: number
) => {
  const unassignedTasks: ChecklistItemWithResourceInfo[] = []
  const todo: ChecklistItemWithResourceInfo[] = []
  const completedTasks: ChecklistItemWithResourceInfo[] = []
  const assignedToMeTasks: ChecklistItemWithResourceInfo[] = []
  const pastDueTasks: ChecklistItemWithResourceInfo[] = []

  tasks.forEach((t) => {
    if (t.owner?.id === userId) assignedToMeTasks.push(t)
    if (t.isCompleted) completedTasks.push(t)
    if (
      t.dueDate &&
      moment(t.dueDate).isBefore(moment().subtract(1, 'days')) &&
      !t.isCompleted
    )
      pastDueTasks.push(t)
    if (!t.isCompleted) todo.push(t)
    if (!t.owner && !t.isCompleted) unassignedTasks.push(t)
  })

  return {
    unassignedTasks,
    todo,
    completedTasks,
    assignedToMeTasks,
    pastDueTasks,
  }
}

export const countVisibleTasks = (
  tasks: ChecklistItemWithResourceInfo[]
): number => {
  const groupedTasks = tasks.reduce((acc, task) => {
    const key = task.checklistItemGroupId || `single_${task.id}`
    if (!acc[key]) {
      acc[key] = []
    }
    acc[key].push(task)
    return acc
  }, {} as Record<string | number, ChecklistItemWithResourceInfo[]>)

  return Object.values(groupedTasks).reduce(
    (count) =>
      // For each group, we count it as one visible task
      count + 1,
    0
  )
}

const sortByDateAscending = (
  a: ChecklistItemWithResourceInfo,
  b: ChecklistItemWithResourceInfo
) => {
  const dateA = a.dueDate || a.createdAt
  const dateB = b.dueDate || b.createdAt
  return moment(dateA).diff(moment(dateB))
}

const getOldestIncompleteTask = (group: ChecklistItemWithResourceInfo[]) => {
  const incompleteTasks = group.filter((task) => task.isCompleted !== true)
  return incompleteTasks.length > 0
    ? incompleteTasks.reduce((a, b) => (sortByDateAscending(a, b) <= 0 ? a : b))
    : group.reduce((a, b) => (sortByDateAscending(a, b) <= 0 ? a : b))
}

export const getTasksForTabbedView = (
  tasks: ChecklistItemWithResourceInfo[],
  activeTab: TabFilterKeyEnum
): ChecklistItemWithResourceInfo[] => {
  let sortedTasks: ChecklistItemWithResourceInfo[] = tasks

  // Apply a unique layer of filtering/sorting logic to recurring tasks in the "All" tabbed view
  if (activeTab === TabFilterKeyEnum.all) {
    const grouped = tasks.reduce((acc, task) => {
      const key = task.checklistItemGroupId || `single_${task.id}`
      acc[key] = [...(acc[key] || []), task]
      return acc
    }, {} as Record<string | number, ChecklistItemWithResourceInfo[]>)

    sortedTasks = Object.values(grouped).map((group) =>
      group.length > 1 ? getOldestIncompleteTask(group) : group[0]
    )
  }

  // Apply consistent sorting for all tabs
  return sortedTasks.sort((a, b) => {
    // First, sort by due date (if exists)
    if (a.dueDate && b.dueDate) return sortByDateAscending(a, b)
    if (a.dueDate) return -1
    if (b.dueDate) return 1

    // If no due dates, sort by creation date
    return moment(a.createdAt).diff(moment(b.createdAt))
  })
}

export const getTabName = (activeTab: TabFilterKeyEnum) => {
  switch (activeTab) {
    case TabFilterKeyEnum.unassigned:
      return 'unassigned'
    case TabFilterKeyEnum.pastDue:
      return 'past due'
    case TabFilterKeyEnum.todo:
      return 'to do'
    case TabFilterKeyEnum.completed:
      return 'completed'
    case TabFilterKeyEnum.assignedToMe:
      return 'assigned'
    case TabFilterKeyEnum.all:
      return 'all'
    default:
      return ''
  }
}

export const buildResourceLink = (resourceInfo: Resource) => {
  if (resourceInfo.type === ResourceType.Course) {
    return `/academy/trainings/saved-resources/${
      (resourceInfo as Course).courseId
    }`
  }

  if (resourceInfo.type === ResourceType.LearningTrack)
    return `/academy/learning-tracks/${resourceInfo.id}`

  return `/academy/resources/search/${resourceInfo.id}`
}

export const buildTaskToastMessage = ({
  isDuplicating,
  isRecurring,
  selectedTask,
}: {
  isDuplicating: boolean
  isRecurring: boolean
  selectedTask: ChecklistItemWithResourceInfo | undefined
}) => {
  let toastMsgType = 'Task'
  let toastMsgAction = 'Added!'
  if (isRecurring || selectedTask?.checklistItemGroupId)
    toastMsgType = 'Recurring Task'
  if (isDuplicating) toastMsgAction = 'Duplicated!'
  else if (selectedTask) toastMsgAction = 'Edited!'

  return `${toastMsgType} ${toastMsgAction}`
}
